import { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, 
  User, MapPin, Calendar, DollarSign,
  Wrench, Droplet, Zap, Trash2, Users,
  ArrowRight, AlertTriangle, Loader2
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BackHeader } from '../components/BackHeader';

interface WorkOrder {
  id: string;
  title: string;
  description: string;
  unit: string;
  category: 'plumbing' | 'electrical' | 'hvac' | 'general' | 'cleaning' | 'gardening';
  priority: 'low' | 'medium' | 'high' | 'emergency';
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  createdBy: string;
  assignedToId?: string;
  assignedToName?: string;
  createdAt: string;
  scheduledDate?: string;
  estimatedCost?: number;
  actualCost?: number;
  photos: string[];
  comments: Comment[];
}

interface Comment {
  id: string;
  author: string;
  text: string;
  timestamp: string;
}

// Maintenance has legacy endpoints with both bare arrays and envelope/paginated payloads.
// Normalize at the boundary so an empty or partially populated response can never crash the page.
const normalizeWorkOrder = (value: any): WorkOrder => ({
  ...value,
  id: String(value?.id ?? value?.request_id ?? ''),
  title: String(value?.title ?? value?.subject ?? value?.name ?? 'Solicitud de mantenimiento'),
  description: String(value?.description ?? ''),
  unit: String(value?.unit ?? value?.unit_number ?? value?.unitName ?? ''),
  category: value?.category in categories ? value.category : 'general',
  priority: value?.priority in priorityConfig ? value.priority : 'medium',
  status: value?.status === 'in_progress' ? 'in-progress' : (value?.status in statusConfig ? value.status : 'pending'),
  createdBy: String(value?.createdBy ?? value?.created_by ?? ''),
  createdAt: value?.createdAt ?? value?.created_at ?? '',
  photos: Array.isArray(value?.photos) ? value.photos : [],
  comments: Array.isArray(value?.comments) ? value.comments : [],
});

const categories: Record<string, { icon: any; label: string; color: string; text: string; bg: string }> = {
  plumbing: { icon: Droplet, label: 'Plomería', color: 'bg-blue-500', text: 'text-blue-400', bg: 'bg-blue-500/10' },
  electrical: { icon: Zap, label: 'Electricidad', color: 'bg-amber-500', text: 'text-amber-400', bg: 'bg-amber-500/10' },
  hvac: { icon: Wrench, label: 'Clima/AC', color: 'bg-cyan-500', text: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  general: { icon: Wrench, label: 'General', color: 'bg-slate-500', text: 'text-[var(--v-text-secondary)]', bg: 'bg-slate-500/10' },
  cleaning: { icon: Trash2, label: 'Limpieza', color: 'bg-emerald-500', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  gardening: { icon: Wrench, label: 'Jardinería', color: 'bg-green-500', text: 'text-green-400', bg: 'bg-green-500/10' },
};

const priorityConfig: Record<string, { label: string; color: string; dot: string }> = {
  low: { label: 'Baja', color: 'bg-slate-500/10 text-[var(--v-text-secondary)] border-slate-500/20', dot: 'bg-slate-400' },
  medium: { label: 'Media', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', dot: 'bg-amber-500' },
  high: { label: 'Alta', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', dot: 'bg-orange-500' },
  emergency: { label: 'Emergencia', color: 'bg-red-500/10 text-red-400 border-red-500/20', dot: 'bg-red-500' },
};

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  pending: { label: 'Pendiente', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  'in-progress': { label: 'En Progreso', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Wrench },
  completed: { label: 'Completada', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'bg-slate-500/10 text-[var(--v-text-secondary)] border-slate-500/20', icon: XCircle },
};

export default function WorkOrders() {
  const { user } = useAuth();
  const condoId = user?.condoId;

  const [orders, setOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadOrders = useCallback(async () => {
    if (!condoId) {
      setLoading(false);
      setOrders([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/maintenance/condo/${condoId}`);
      const data = res?.data?.data ?? res?.data ?? [];
      const ordersList = Array.isArray(data)
        ? data
        : (data?.items || data?.requests || data?.workOrders || data?.maintenanceRequests || []);
      setOrders(Array.isArray(ordersList) ? ordersList.filter(Boolean).map(normalizeWorkOrder) : []);
    } catch (err: any) {
      // If we get 401 or other errors, show empty state instead of crashing
      console.error('Error loading work orders:', err);
      setOrders([]);
      // Don't show error for 401 — the interceptor handles auth redirects
      if (err?.response?.status !== 401) {
        setError(err?.response?.data?.message || 'No se pudieron cargar las órdenes de trabajo');
      }
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const getCategoryIcon = (category: string) => {
    const cat = categories[category];
    const Icon = cat?.icon || Wrench;
    return (
      <div className={`w-10 h-10 ${cat?.color || 'bg-slate-500'} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || order.category === filterCategory;
    const matchesSearch = (order.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (order.unit || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesCategory && matchesSearch;
  });

  const stats = {
    pending: orders.filter(o => o.status === 'pending').length,
    inProgress: orders.filter(o => o.status === 'in-progress').length,
    completed: orders.filter(o => o.status === 'completed').length,
    emergency: orders.filter(o => o.priority === 'emergency' && o.status !== 'completed').length,
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback='/services/dashboard' />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Órdenes de Trabajo</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Gestiona solicitudes de mantenimiento y reparaciones</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowNewOrder(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white rounded-lg font-medium transition-all"
          >
            <Plus className="w-5 h-5" />
            Nueva Orden
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-sm text-[var(--v-text-secondary)] font-medium">Pendientes</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Wrench className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm text-[var(--v-text-secondary)] font-medium">En Progreso</span>
          </div>
          <p className="text-2xl font-bold text-blue-400">{stats.inProgress}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm text-[var(--v-text-secondary)] font-medium">Completadas</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.completed}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-sm text-[var(--v-text-secondary)] font-medium">Emergencias</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.emergency}</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/30 rounded-lg p-3 flex items-center gap-2 text-[var(--v-danger)]">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-[var(--v-danger)] hover:opacity-70">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[var(--v-text-secondary)]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando órdenes...
        </div>
      )}

      {/* Filters */}
      {!loading && orders.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-5 h-5 text-[var(--v-text-tertiary)] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar órdenes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)] placeholder-[var(--v-text-tertiary)]"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)]"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="in-progress">En Progreso</option>
            <option value="completed">Completada</option>
            <option value="cancelled">Cancelada</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)]"
          >
            <option value="all">Todas las categorías</option>
            {Object.entries(categories).map(([key, cat]) => (
              <option key={key} value={key}>{cat.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Orders List */}
      {!loading && (
        <div className="space-y-3">
          {filteredOrders.length === 0 ? (
            <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
              <Wrench className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">
                {orders.length === 0 ? 'No tienes órdenes de trabajo' : 'No hay órdenes'}
              </h3>
              <p className="text-[var(--v-text-secondary)]">
                {orders.length === 0 
                  ? 'Crea una nueva orden de trabajo para solicitar mantenimiento.' 
                  : 'No se encontraron órdenes de trabajo con los filtros seleccionados.'}
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => {
              const StatusIcon = statusConfig[order.status]?.icon || Clock;
              return (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm hover:shadow-md hover:border-[var(--v-teal-400)]/30 cursor-pointer transition-all"
                >
                  <div className="flex items-start gap-4">
                    {getCategoryIcon(order.category)}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="text-base font-semibold text-[var(--v-text-primary)]">{order.title}</h4>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig[order.status]?.color || ''}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig[order.status]?.label || order.status}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${priorityConfig[order.priority]?.color || ''}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${priorityConfig[order.priority]?.dot || ''}`}></span>
                          {priorityConfig[order.priority]?.label || order.priority}
                        </span>
                      </div>
                      <p className="text-[var(--v-text-secondary)] text-sm mb-3">{order.description}</p>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--v-text-tertiary)]">
                        {order.unit && (
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            {order.unit}
                          </span>
                        )}
                        {order.createdAt && (
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(order.createdAt).toLocaleDateString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5" />
                          {order.assignedToName || 'Sin asignar'}
                        </span>
                        {order.estimatedCost && (
                          <span className="flex items-center gap-1.5 text-[var(--v-teal-400)] font-medium">
                            <DollarSign className="w-3.5 h-3.5" />
                            ${order.estimatedCost.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[var(--v-text-tertiary)] flex-shrink-0" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-start justify-between">
              <div className="flex items-start gap-4">
                {getCategoryIcon(selectedOrder.category)}
                <div>
                  <h3 className="text-xl font-semibold text-[var(--v-text-primary)]">{selectedOrder.title}</h3>
                  <p className="text-[var(--v-text-tertiary)] text-sm mt-1">{selectedOrder.id}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedOrder(null)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Priority */}
              <div className="flex flex-wrap gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusConfig[selectedOrder.status]?.color || ''}`}>
                  {statusConfig[selectedOrder.status]?.label}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${priorityConfig[selectedOrder.priority]?.color || ''}`}>
                  Prioridad: {priorityConfig[selectedOrder.priority]?.label}
                </span>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--v-surface-2)] p-4 rounded-xl">
                  <p className="text-xs text-[var(--v-text-tertiary)] uppercase font-medium mb-1">Ubicación</p>
                  <p className="text-[var(--v-text-primary)] font-medium">{selectedOrder.unit || 'N/A'}</p>
                </div>
                <div className="bg-[var(--v-surface-2)] p-4 rounded-xl">
                  <p className="text-xs text-[var(--v-text-tertiary)] uppercase font-medium mb-1">Solicitante</p>
                  <p className="text-[var(--v-text-primary)] font-medium">{selectedOrder.createdBy || 'N/A'}</p>
                </div>
                <div className="bg-[var(--v-surface-2)] p-4 rounded-xl">
                  <p className="text-xs text-[var(--v-text-tertiary)] uppercase font-medium mb-1">Asignado a</p>
                  <p className="text-[var(--v-text-primary)] font-medium">{selectedOrder.assignedToName || 'Sin asignar'}</p>
                </div>
                <div className="bg-[var(--v-surface-2)] p-4 rounded-xl">
                  <p className="text-xs text-[var(--v-text-tertiary)] uppercase font-medium mb-1">Costo Estimado</p>
                  <p className="text-[var(--v-text-primary)] font-medium">{selectedOrder.estimatedCost ? `$${selectedOrder.estimatedCost.toLocaleString()}` : 'Por determinar'}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="text-sm font-semibold text-[var(--v-text-primary)] mb-2">Descripción</h4>
                <p className="text-[var(--v-text-secondary)] bg-[var(--v-surface-2)] p-4 rounded-xl">{selectedOrder.description}</p>
              </div>

              {/* Comments */}
              {selectedOrder.comments && selectedOrder.comments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[var(--v-text-primary)] mb-3">Comentarios</h4>
                  <div className="space-y-3">
                    {selectedOrder.comments.map(comment => (
                      <div key={comment.id} className="bg-[var(--v-surface-2)] p-4 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-[var(--v-text-primary)]">{comment.author}</span>
                          <span className="text-xs text-[var(--v-text-tertiary)]">{comment.timestamp}</span>
                        </div>
                        <p className="text-[var(--v-text-secondary)] text-sm">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-[var(--v-border-light)] bg-[var(--v-surface-2)] flex justify-end gap-3">
              <button 
                onClick={() => setSelectedOrder(null)}
                className="px-4 py-2.5 border border-[var(--v-border-strong)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-3)] transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Order Modal */}
      {showNewOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Nueva Orden de Trabajo</h3>
              <button 
                onClick={() => setShowNewOrder(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form className="p-6 space-y-4" onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              const title = (formData.get('title') as string)?.trim();
              if (!title) return;
              try {
                // #366 fix: BE real CRUD is POST /maintenance/condo/:condoId
                // (unitId, category, description required; title folds into description).
                const unitRaw = (formData.get('unit') as string) || '';
                const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(unitRaw);
                const descr = (formData.get('description') as string) || '';
                await api.post(`/maintenance/condo/${condoId}`, {
                  ...(isUuid ? { unitId: unitRaw } : {}),
                  category: formData.get('category') as string,
                  description: descr ? `${title}\n\n${descr}` : title,
                  priority: formData.get('priority') as string,
                });
                setShowNewOrder(false);
                loadOrders();
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Error al crear orden');
              }
            }}>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Título</label>
                <input 
                  type="text" 
                  name="title"
                  required
                  className="w-full px-4 py-2.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)] placeholder-[var(--v-text-tertiary)]"
                  placeholder="Ej: Fuga en baño principal"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Categoría</label>
                  <select name="category" className="w-full px-4 py-2.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)]">
                    {Object.entries(categories).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Prioridad</label>
                  <select name="priority" className="w-full px-4 py-2.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)]">
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                    <option value="emergency">Emergencia</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Ubicación/Unidad</label>
                <input 
                  type="text" 
                  name="unit"
                  className="w-full px-4 py-2.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)] placeholder-[var(--v-text-tertiary)]"
                  placeholder="Ej: Torre A, 305"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-secondary)] mb-2">Descripción</label>
                <textarea 
                  name="description"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] text-[var(--v-text-primary)] placeholder-[var(--v-text-tertiary)] resize-none"
                  placeholder="Describe el problema..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowNewOrder(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-strong)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white rounded-lg font-medium transition-colors"
                >
                  Crear Orden
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
